// askSmart-ui/src/utils/smrtdocsPdf.ts
//
// Builds the final signed PDF on the last signer's submit. Each
// placement is embedded into a per-type box at the stored x/y on the
// appropriate page. The signed output is intentionally compact —
// signature and initial blocks fit entirely within the placement box
// (image + a small "Signed by" line + a subtle verification code) so
// they don't bleed onto the surrounding document content.

import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

// Mirrors the placement modal's SIGNER_COLORS so the left-edge bar drawn
// behind each signature / initial block uses the same hue the admin saw
// when placing.
const SIGNER_COLORS_HEX = [
  "#4a90e2",
  "#e2724a",
  "#50c878",
  "#b768e2",
  "#e2c14a",
];

const hexToRgb = (hex: string) => {
  const h = hex.replace("#", "");
  return rgb(
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  );
};

const formatShortDate = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).slice(0, 10);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
};

export interface SignedPdfPlacement {
  id: string;
  type: string;
  signerIndex: number;
  x: number;
  y: number;
  page?: number;
}

export interface SignedPdfSigner {
  name?: string;
  signedAt?: string | null;
  signatureImages?: Record<string, string>;
  // Non-signature placement values keyed by placement.id. Powers the
  // embed of initial / date / title / text fields so the final PDF
  // mirrors what the signer filled in.
  fieldValues?: Record<string, string>;
  // SHA256(documentHash + userId + signedAt). Stored full; only the
  // first 12 chars are rendered as a subtle audit reference inside the
  // signature and initial blocks.
  verificationCode?: string;
}

// SHA256 of a plain UTF-8 string, returned as lowercase hex. Used to
// derive the per-signer verification code from documentHash + userId +
// signedAt. Deterministic — same inputs always produce the same code.
export async function sha256OfString(s: string): Promise<string> {
  const bytes = new TextEncoder().encode(s);
  const buf = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export interface BuildSignedPdfParams {
  originalBytes: Uint8Array;
  placements: SignedPdfPlacement[];
  signers: SignedPdfSigner[];
  // When false/omitted (the default, used for the durable signed PDF that is
  // uploaded to storage), text-encrypted fields are stamped as a fixed mask so
  // the plaintext SIN / banking / licence value never lands in the blob. When
  // true (the audited "download unmasked copy" path), the caller must have
  // substituted the decrypted plaintext into signer.fieldValues and those real
  // values are stamped instead.
  revealEncrypted?: boolean;
}

// Mask stamped into the signed PDF for encrypted fields. Bullets are within
// the WinAnsi range so StandardFonts.Helvetica renders them without throwing.
const ENCRYPTED_FIELD_MASK = "••••••••";

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const comma = dataUrl.indexOf(",");
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export async function buildSignedPdf({
  originalBytes,
  placements,
  signers,
  revealEncrypted = false,
}: BuildSignedPdfParams): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.load(originalBytes);
  const pages = pdfDoc.getPages();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  // The placement modal renders each page at 800 CSS pixels wide. Per-
  // type pixel footprints (matched against FIELD_DIMS in the placement
  // modal) are scaled to the real page width so the embedded output
  // lines up with what the admin saw when placing.
  const CANVAS_W = 800;
  const FIELD_DIMS: Record<string, { w: number; h: number }> = {
    signature: { w: 140, h: 36 },
    initial: { w: 70, h: 36 },
    date: { w: 90, h: 22 },
    title: { w: 150, h: 22 },
    text: { w: 180, h: 22 },
  };

  // Shrink a string until it fits boxW at the given font size, adding
  // an ellipsis if anything was trimmed. Keeps text strictly inside
  // the placement box so it never spills onto neighbouring document
  // content.
  const fitText = (s: string, size: number, maxWidth: number): string => {
    if (font.widthOfTextAtSize(s, size) <= maxWidth) return s;
    let lo = 0;
    let hi = s.length;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (font.widthOfTextAtSize(s.slice(0, mid) + "…", size) <= maxWidth) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    return lo > 0 ? s.slice(0, lo) + "…" : "";
  };

  for (const p of placements) {
    const signer = signers[p.signerIndex];
    if (!signer) continue;
    const pageIdx = typeof p.page === "number" ? p.page : 0;
    const page = pages[pageIdx];
    if (!page) continue;
    const { width: pageW, height: pageH } = page.getSize();

    const dims = FIELD_DIMS[p.type] || FIELD_DIMS.text;
    const boxW = pageW * (dims.w / CANVAS_W);
    const boxH = pageW * (dims.h / CANVAS_W);

    // CSS coords are top-left origin; PDF coords are bottom-left.
    const pdfX = p.x * pageW;
    const pdfY = pageH - p.y * pageH - boxH;

    const code = signer.verificationCode
      ? signer.verificationCode.slice(0, 12).toUpperCase()
      : "";

    const signerColor = hexToRgb(
      SIGNER_COLORS_HEX[p.signerIndex % SIGNER_COLORS_HEX.length],
    );
    const barW = 1.5;
    const contentLeft = pdfX + barW + 2;
    const contentW = boxW - barW - 4;

    if (p.type === "signature") {
      const dataUrl = signer.signatureImages?.[p.id];
      if (!dataUrl) continue;
      const bytes = dataUrlToBytes(dataUrl);
      let img;
      try {
        img = await pdfDoc.embedPng(bytes);
      } catch {
        try {
          img = await pdfDoc.embedJpg(bytes);
        } catch {
          continue;
        }
      }

      const labelSize = Math.max(5, boxH * 0.2);
      const codeSize = Math.max(4, boxH * 0.16);
      const imageH = Math.max(6, boxH - labelSize - codeSize - 3);

      // Left signer-color bar (subtle, thin).
      page.drawRectangle({
        x: pdfX,
        y: pdfY,
        width: barW,
        height: boxH,
        color: signerColor,
      });

      // Top row: "Signed By:" left, signing date right.
      const dateStr = formatShortDate(signer.signedAt);
      page.drawText("Signed By:", {
        x: contentLeft,
        y: pdfY + boxH - labelSize,
        size: labelSize,
        font,
      });
      if (dateStr) {
        const dateLine = fitText(dateStr, labelSize, contentW);
        const dateW = font.widthOfTextAtSize(dateLine, labelSize);
        page.drawText(dateLine, {
          x: contentLeft + contentW - dateW,
          y: pdfY + boxH - labelSize,
          size: labelSize,
          font,
        });
      }

      // Middle: handwriting image (rendered upstream as just the
      // signature, no baked-in label).
      page.drawImage(img, {
        x: contentLeft,
        y: pdfY + codeSize + 1,
        width: contentW,
        height: imageH,
      });
      if (code) {
        page.drawText(fitText(code, codeSize, contentW), {
          x: contentLeft,
          y: pdfY + 1,
          size: codeSize,
          font,
        });
      }
    } else if (p.type === "initial") {
      const codeSize = Math.max(4, boxH * 0.16);
      // Accept initials as either an embedded image (when the signing
      // view captures them on canvas) or a typed handwriting-font
      // string carried in fieldValues. Either way the verification
      // code is rendered beneath so the audit reference is preserved.
      const fieldValue = signer.fieldValues?.[p.id];
      const imageUrl =
        signer.signatureImages?.[p.id] ||
        (fieldValue && fieldValue.startsWith("data:image")
          ? fieldValue
          : undefined);
      const textValue =
        !imageUrl && fieldValue && fieldValue.trim() ? fieldValue : "";

      const contentH = Math.max(6, boxH - codeSize - 2);
      const contentBottom = pdfY + codeSize + 1;

      // Left signer-color bar (matches signature treatment).
      page.drawRectangle({
        x: pdfX,
        y: pdfY,
        width: barW,
        height: boxH,
        color: signerColor,
      });

      if (imageUrl) {
        const bytes = dataUrlToBytes(imageUrl);
        let img;
        try {
          img = await pdfDoc.embedPng(bytes);
        } catch {
          try {
            img = await pdfDoc.embedJpg(bytes);
          } catch {
            img = undefined;
          }
        }
        if (img) {
          page.drawImage(img, {
            x: contentLeft,
            y: contentBottom,
            width: contentW,
            height: contentH,
          });
        }
      } else if (textValue) {
        const textSize = Math.max(7, contentH * 0.7);
        const initialsLine = fitText(
          textValue.slice(0, 8),
          textSize,
          contentW,
        );
        page.drawText(initialsLine, {
          x: contentLeft,
          y: contentBottom + (contentH - textSize) / 2,
          size: textSize,
          font,
          color: rgb(0.102, 0.302, 0.678),
        });
      }

      if (code) {
        page.drawText(fitText(code, codeSize, contentW), {
          x: contentLeft,
          y: pdfY + 1,
          size: codeSize,
          font,
        });
      }
    } else {
      // date / title / text / text-encrypted — single line of the captured
      // value, sized to the box and centered vertically. No verification
      // block on these (audit reference lives on signature + initial only).
      const raw = signer.fieldValues?.[p.id];
      let text: string;
      if (p.type === "text-encrypted") {
        // The value is present when the signer filled it (a non-empty
        // plaintext string at submit time, or a stored envelope object on a
        // later rebuild). Either way the durable output is masked unless the
        // caller explicitly opted into revealing decrypted plaintext.
        const hasValue =
          raw != null &&
          (typeof raw === "string" ? raw.trim() !== "" : true);
        if (!hasValue) continue;
        text =
          revealEncrypted && typeof raw === "string"
            ? raw
            : ENCRYPTED_FIELD_MASK;
      } else {
        if (!raw || !String(raw).trim()) continue;
        text = String(raw);
      }
      const textSize = Math.max(6, boxH * 0.55);
      const line = fitText(text, textSize, boxW - 4);
      if (!line) continue;
      page.drawText(line, {
        x: pdfX + 2,
        y: pdfY + (boxH - textSize) / 2,
        size: textSize,
        font,
      });
    }
  }

  return await pdfDoc.save();
}
